import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { propertiesAPI } from '../services/api';
import { ChevronLeft, Loader2, Save, Upload, X, ImagePlus, ShieldAlert } from 'lucide-react';
import RoomsEditor from '../components/RoomsEditor';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return path;
};

const AMENITIES = [
  'WiFi', 'Kitchen', 'Free parking', 'Air conditioning', 'Heating',
  'Pool', 'Beach access', 'Mountain view', 'Ocean view', 'Lake view',
  'Fireplace', 'BBQ grill', 'Washer', 'Pet friendly', 'Garden'
];

const PropertyEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState(null);
  const [panVerified, setPanVerified] = useState(false);

  // existingImages: paths already saved on server
  const [existingImages, setExistingImages] = useState([]);
  // newFiles: File objects selected by user for upload
  const [newFiles, setNewFiles] = useState([]);

  const [formData, setFormData] = useState({
    title: '', location: '', price: '', description: '',
    guests: 2, bedrooms: 1, beds: 1, bathrooms: 1, amenities: [],
    listing_type: 'entire',
    rooms: [],
  });

  const hostId = localStorage.getItem('hostId');

  useEffect(() => {
    if (!hostId) {
      toast.error('Please login as a host');
      navigate('/host/register');
      return;
    }
    fetchProperty();
    // Check PAN verification for edit gating
    const checkPan = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/host/verification-status/${hostId}`);
        const data = await res.json();
        if (data?.success) setPanVerified(!!data.data?.verified);
      } catch (_e) {}
    };
    checkPan();
  }, [id, hostId]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const response = await propertiesAPI.getById(id);
      if (response.success) {
        const prop = response.data;
        if (prop.host_id !== hostId) {
          toast.error('You are not authorized to edit this property');
          navigate('/host/dashboard');
          return;
        }
        setProperty(prop);
        setFormData({
          title: prop.title || '', location: prop.location || '',
          price: prop.price || '', description: prop.description || '',
          guests: prop.guests || 2, bedrooms: prop.bedrooms || 1,
          beds: prop.beds || 1, bathrooms: prop.bathrooms || 1,
          amenities: prop.amenities || [],
          listing_type: prop.listing_type || 'entire',
          rooms: prop.rooms || [],
        });
        setExistingImages(prop.images || []);
      }
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/host/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    const arr = formData.amenities.includes(amenity)
      ? formData.amenities.filter(a => a !== amenity)
      : [...formData.amenities, amenity];
    updateFormData('amenities', arr);
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + newFiles.length + files.length;
    if (totalImages > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }
    setNewFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!panVerified) { toast.error('Please verify your PAN before editing this property'); return; }
    if (!formData.title.trim()) { toast.error('Please enter property title'); return; }
    if (existingImages.length + newFiles.length === 0) { toast.error('Please add at least one image'); return; }

    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('host_id', hostId);
      fd.append('title', formData.title);
      fd.append('location', formData.location);
      fd.append('price', formData.price);
      fd.append('description', formData.description);
      fd.append('guests', formData.guests);
      fd.append('bedrooms', formData.bedrooms);
      fd.append('beds', formData.beds);
      fd.append('bathrooms', formData.bathrooms);
      fd.append('amenities', formData.amenities.join(','));
      fd.append('listing_type', formData.listing_type);
      if (formData.listing_type === 'rooms') {
        fd.append('rooms_json', JSON.stringify((formData.rooms || []).map((r) => ({
          room_id: r.room_id,
          category: r.category,
          beds: Number(r.beds) || 1,
          bathrooms: Number(r.bathrooms) || 1,
          max_guests: Number(r.max_guests) || 2,
          price: Number(r.price) || 0,
          inventory: Number(r.inventory) || 1,
          amenities: r.amenities || [],
          description: r.description || '',
          photos: r.photos || [],
        }))));
      }
      fd.append('existingImages', existingImages.join(','));

      newFiles.forEach(file => fd.append('newImages', file));

      const response = await axios.post(
        `${BACKEND_URL}/api/properties/${id}/update-with-images`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        if (response.data.pending_review) {
          toast.success('Edits submitted for admin review. Your listing stays live with the previous content until approved.', { duration: 6000 });
        } else {
          toast.success('Property updated successfully!');
        }
        navigate('/host/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#7CB342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/host/dashboard')} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold">Edit Property</h1>
          <p className="text-gray-500">{property?.title}</p>
        </div>

        {property?.pending_edit?.status === 'pending' && (
          <Card className="mb-6 bg-blue-50 border-blue-200" data-testid="pending-edit-banner">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900">Edits pending admin review</p>
                  <p className="text-xs text-blue-800 mt-1">
                    You have edits submitted on {new Date(property.pending_edit.submitted_at).toLocaleDateString()} awaiting admin approval.
                    Saving new changes here will replace that pending edit. Your live listing continues to show the previously approved content.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {property?.last_rejected_edit && !property?.pending_edit && (
          <Card className="mb-6 bg-red-50 border-red-200" data-testid="rejected-edit-banner">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-900">Your last edit was rejected</p>
                  <p className="text-xs text-red-800 mt-1">
                    <strong>Admin note:</strong> {property.last_rejected_edit.rejection_reason || '—'}
                  </p>
                  <p className="text-xs text-red-800 mt-1">
                    Please revise the details below and resubmit for review.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!panVerified && (
          <Card className="mb-6 bg-orange-50 border-orange-200" data-testid="edit-pan-banner">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-orange-900">PAN verification required to save changes</p>
                  <p className="text-xs text-orange-800 mt-1">
                    For trust and compliance, you must verify your PAN before editing your homestay. You can still browse the form below.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => navigate('/host/dashboard')}
                    className="mt-3 bg-[#7CB342] hover:bg-[#6da036] text-white"
                  >
                    Verify PAN now
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader><CardTitle>Basic Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Property Title *</Label>
                <Input id="title" value={formData.title} onChange={(e) => updateFormData('title', e.target.value)} className="mt-2" data-testid="edit-title" />
              </div>
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input id="location" value={formData.location} onChange={(e) => updateFormData('location', e.target.value)} className="mt-2" data-testid="edit-location" />
              </div>
              <div>
                <Label htmlFor="price">Price per Night (INR) *</Label>
                <Input id="price" type="number" value={formData.price} onChange={(e) => updateFormData('price', e.target.value)} className="mt-2 w-40" data-testid="edit-price" />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => updateFormData('description', e.target.value)} className="mt-2" rows={4} data-testid="edit-description" />
              </div>
            </CardContent>
          </Card>

          {/* Capacity */}
          <Card className="mb-6">
            <CardHeader><CardTitle>Property Details</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['guests', 'bedrooms', 'beds', 'bathrooms'].map(field => (
                  <div key={field}>
                    <Label htmlFor={field}>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
                    <Input id={field} type="number" min={field === 'bedrooms' ? 0 : 1} value={formData[field]} onChange={(e) => updateFormData(field, e.target.value)} className="mt-2" data-testid={`edit-${field}`} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Images */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Property Images</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Upload images from your device. Maximum 10 images.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing images */}
              {existingImages.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">Current Images</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {existingImages.map((img, index) => (
                      <div key={`existing-${index}`} className="relative group aspect-square rounded-lg overflow-hidden border">
                        <img
                          src={resolveImageUrl(img)}
                          alt={`Property ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=200'; }}
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-existing-img-${index}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New file previews */}
              {newFiles.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">New Images to Upload</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {newFiles.map((file, index) => (
                      <div key={`new-${index}`} className="relative group aspect-square rounded-lg overflow-hidden border border-green-300">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`New ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeNewFile(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-new-img-${index}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <span className="absolute bottom-1 left-1 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded">New</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload button */}
              {existingImages.length + newFiles.length < 10 && (
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-8 border-2 border-dashed hover:border-[#7CB342] hover:bg-green-50 transition-colors"
                    data-testid="upload-images-btn"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <ImagePlus className="h-6 w-6 text-gray-400" />
                      <span className="text-gray-500">Click to upload images</span>
                      <span className="text-xs text-gray-400">{existingImages.length + newFiles.length}/10 images</span>
                    </div>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Amenities */}
          <Card className="mb-6">
            <CardHeader><CardTitle>Amenities</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {AMENITIES.map(amenity => (
                  <div key={amenity} className="flex items-center space-x-2">
                    <Checkbox id={amenity} checked={formData.amenities.includes(amenity)} onCheckedChange={() => toggleAmenity(amenity)} />
                    <Label htmlFor={amenity} className="cursor-pointer">{amenity}</Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Rooms editor (only for per-room properties) */}
          {formData.listing_type === 'rooms' && (
            <Card className="mb-6">
              <CardHeader><CardTitle>Room Types</CardTitle></CardHeader>
              <CardContent>
                <RoomsEditor
                  rooms={formData.rooms}
                  onChange={(next) => setFormData((p) => ({ ...p, rooms: next }))}
                  testIdPrefix="edit-room"
                />
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <div className="flex gap-4 mb-12">
            <Button type="button" variant="outline" onClick={() => navigate('/host/dashboard')} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving || !panVerified} title={!panVerified ? 'Verify your PAN first' : ''} className="flex-1 bg-[#7CB342] hover:bg-[#6da036] text-white disabled:opacity-60" data-testid="save-property-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : !panVerified ? <><ShieldAlert className="h-4 w-4 mr-2" />Verify PAN to Save</> : <><Save className="h-4 w-4 mr-2" />Save Changes</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyEditPage;
